import { HATA_MESAJLARI, type HataKodu } from '@hotelcms/shared';
import { denemeArttir, kuyrugaEkle, kuyruguOku, kuyruktanSil, yeniIstekId } from './offline.js';

export class ApiHatasi extends Error {
  constructor(
    public readonly kod: HataKodu | 'AG_HATASI',
    mesaj: string,
    public readonly status = 0,
    public readonly alan?: string,
    /**
     * Sunucunun hataya ekledigi yapisal ayrinti.
     *
     * Bazi hatalar yalnizca "olmadi" demez, ne yapilacagini da tasir:
     * oda limiti dolunca bir sonraki paketin ne getirdigi buradan gelir ve
     * paywall bunu gosterir. Mesaji ayristirmak yerine veriyi tasiyoruz.
     */
    public readonly detay?: unknown,
  ) {
    super(mesaj);
  }
}

/**
 * Gelistirme/staging kolayligi.
 *
 * Uretimde kiraci alt alan adindan belirlenir (alaotel.otelcms.com) ve bu
 * mekanizma sunucuda tamamen kapalidir. workers.dev veya localhost uzerinde
 * ise ?tenant=demo ile hangi tesisin paneline bakildigi secilebilir.
 */
const TENANT_ANAHTARI = 'hotelcms.tenant';

function devTenantSlug(): string | null {
  const url = new URL(location.href);
  const sorgudan = url.searchParams.get('tenant');
  if (sorgudan) sessionStorage.setItem(TENANT_ANAHTARI, sorgudan);
  return sessionStorage.getItem(TENANT_ANAHTARI);
}

const TOKEN_ANAHTARI = 'hotelcms.token';
const YENILEME_ANAHTARI = 'hotelcms.refresh';

/**
 * Misafir mobil oturumu (odadaki QR + kimlik dogrulamasi).
 *
 * Personel token'indan AYRI tutulur: farkli bir kimlik, farkli bir omur ve
 * farkli bir baslik. sessionStorage'dadir, yani sekme kapaninca biter -
 * ortak kullanilan bir telefonda bir sonraki misafire acik kalmasin.
 * Burada okunmasinin sebebi cevrimdisi kuyrugun da ayni basligi
 * tasiyabilmesi: baglanti gelince tekrar gonderilen talep dogrulanmis kalir.
 */
const MISAFIR_ANAHTARI = 'hotelcms.misafirOturum';

export const misafirOturum = {
  oku: () => {
    try {
      return sessionStorage.getItem(MISAFIR_ANAHTARI);
    } catch {
      return null;
    }
  },
  yaz(anahtar: string) {
    try {
      sessionStorage.setItem(MISAFIR_ANAHTARI, anahtar);
    } catch {
      /* gizli sekmede depolama kapali olabilir */
    }
  },
  temizle() {
    try {
      sessionStorage.removeItem(MISAFIR_ANAHTARI);
    } catch {
      /* yoksay */
    }
  },
};

/**
 * Oturum saklama.
 *
 * "Bu cihazda oturumu açık tut" isaretliyse localStorage (tarayici kapansa da
 * kalir), degilse sessionStorage (sekme kapaninca biter). Ortak bir cihazda
 * calisan resepsiyon icin bu ayrim gercekten onemlidir.
 */
function depo(kalici: boolean): Storage {
  return kalici ? localStorage : sessionStorage;
}

export const oturum = {
  token: () => sessionStorage.getItem(TOKEN_ANAHTARI) ?? localStorage.getItem(TOKEN_ANAHTARI),
  refresh: () => sessionStorage.getItem(YENILEME_ANAHTARI) ?? localStorage.getItem(YENILEME_ANAHTARI),
  yaz(access: string, refresh: string, kalici = true) {
    oturum.temizle();
    const d = depo(kalici);
    d.setItem(TOKEN_ANAHTARI, access);
    d.setItem(YENILEME_ANAHTARI, refresh);
  },
  temizle() {
    for (const d of [localStorage, sessionStorage]) {
      d.removeItem(TOKEN_ANAHTARI);
      d.removeItem(YENILEME_ANAHTARI);
    }
  },
};

async function cevabiCoz<T>(cevap: Response): Promise<T> {
  if (cevap.ok) return (await cevap.json()) as T;
  let kod: HataKodu = 'SUNUCU_HATASI';
  let mesaj: string = HATA_MESAJLARI.SUNUCU_HATASI;
  let alan: string | undefined;
  let detay: unknown;
  try {
    const govde = (await cevap.json()) as {
      hata?: { kod: HataKodu; mesaj: string; alan?: string; detay?: unknown };
    };
    if (govde.hata) {
      kod = govde.hata.kod;
      mesaj = govde.hata.mesaj;
      alan = govde.hata.alan;
      detay = govde.hata.detay;
    }
  } catch {
    /* govde JSON degil */
  }
  throw new ApiHatasi(kod, mesaj, cevap.status, alan, detay);
}

interface IstekSecenek extends RequestInit {
  /** Cevrimdisi kalinirsa istegi kuyruga al (check-in gibi kritik yazma islemleri) */
  cevrimdisiKuyrukla?: boolean;
  idempotencyKey?: string;
}

export async function api<T>(yol: string, secenek: IstekSecenek = {}): Promise<T> {
  const { cevrimdisiKuyrukla, idempotencyKey, ...init } = secenek;
  const basliklar = new Headers(init.headers);
  const token = oturum.token();
  if (token) basliklar.set('authorization', `Bearer ${token}`);
  const tenant = devTenantSlug();
  if (tenant) basliklar.set('x-tenant-slug', tenant);
  const misafir = misafirOturum.oku();
  if (misafir) basliklar.set('x-misafir-oturum', misafir);
  if (idempotencyKey) basliklar.set('idempotency-key', idempotencyKey);
  if (init.body && !(init.body instanceof FormData)) basliklar.set('content-type', 'application/json');

  try {
    const cevap = await fetch(yol, { ...init, headers: basliklar });
    if (cevap.status === 401 && oturum.refresh()) {
      const yenilendi = await tokenYenile();
      if (yenilendi) return api<T>(yol, secenek);
    }
    return await cevabiCoz<T>(cevap);
  } catch (e) {
    if (e instanceof ApiHatasi) throw e;
    // Ag hatasi: kritik yazma islemi ise kuyruga al
    if (cevrimdisiKuyrukla && init.method === 'POST' && typeof init.body === 'string') {
      const id = idempotencyKey ?? yeniIstekId();
      await kuyrugaEkle({ id, yol, govde: JSON.parse(init.body), olusturmaAni: Date.now() });
      throw new ApiHatasi('CEVRIMDISI', HATA_MESAJLARI.CEVRIMDISI);
    }
    throw new ApiHatasi('AG_HATASI', HATA_MESAJLARI.CEVRIMDISI);
  }
}

async function tokenYenile(): Promise<boolean> {
  const refresh = oturum.refresh();
  if (!refresh) return false;
  try {
    const cevap = await fetch('/api/auth/yenile', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refresh }),
    });
    if (!cevap.ok) {
      oturum.temizle();
      return false;
    }
    const govde = (await cevap.json()) as { access: string; refresh: string };
    // Yenileme, kullanicinin sectigi saklama yerini degistirmemeli
    const kalici = localStorage.getItem(TOKEN_ANAHTARI) !== null;
    oturum.yaz(govde.access, govde.refresh, kalici);
    return true;
  } catch {
    return false;
  }
}

/** multipart gonderimi (kimlik gorseli). */
export async function apiForm<T>(yol: string, form: FormData): Promise<T> {
  return api<T>(yol, { method: 'POST', body: form });
}

/** Baglanti gelince bekleyen check-in'leri gonderir. */
export async function kuyruguIsle(): Promise<{ gonderilen: number; kalan: number }> {
  const bekleyenler = await kuyruguOku();
  let gonderilen = 0;
  for (const kayit of bekleyenler) {
    try {
      await api(kayit.yol, {
        method: 'POST',
        body: JSON.stringify(kayit.govde),
        idempotencyKey: kayit.id,
      });
      await kuyruktanSil(kayit.id);
      gonderilen++;
    } catch (e) {
      // Kalici veri hatasinda kuyrukta dondurmeyiz
      if (e instanceof ApiHatasi && e.status >= 400 && e.status < 500 && e.status !== 429) {
        await kuyruktanSil(kayit.id);
      } else {
        await denemeArttir(kayit);
      }
    }
  }
  return { gonderilen, kalan: (await kuyruguOku()).length };
}

export function kuyrukIzleyiciBaslat(bildir?: (sonuc: { gonderilen: number; kalan: number }) => void): () => void {
  const calistir = () => {
    void kuyruguIsle().then((s) => bildir?.(s));
  };
  window.addEventListener('online', calistir);
  const zamanlayici = window.setInterval(calistir, 60_000);
  calistir();
  return () => {
    window.removeEventListener('online', calistir);
    window.clearInterval(zamanlayici);
  };
}
